import { Link } from "react-router-dom";
import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";

const FAQS = [
  { q: "How much does tree removal cost in Northern Virginia?", a: "Tree removal in Northern Virginia typically ranges from $350 for small trees to $2,500+ for large, complex jobs. Factors include tree height, diameter, location, and proximity to structures. We provide free on-site estimates." },
  { q: "How long does tree removal take?", a: "Most residential tree removals are completed in 2–6 hours. Large trees near structures or requiring crane work may take a full day. We work efficiently and include complete cleanup." },
  { q: "Is tree removal covered by homeowner's insurance?", a: "If a tree damages your structure, insurance often covers removal. For hazardous trees or routine removal, it typically is not covered. We can provide documentation to support your insurance claim." },
  { q: "Do you grind the stump after removal?", a: "Stump grinding is an add-on service. We can remove the stump on the same visit or schedule a separate grinding appointment. Ask about our bundle pricing for tree removal + stump grinding." },
];

export default function TreeRemovalPage() {
  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/3cb8d34d0c49ec557f86a3eac00ebf88.jpg"
              alt="Tree removal service Northern Virginia"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a2e]/90 to-[#1a3a2e]/40"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-20">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 text-[#e8632a] text-sm font-bold uppercase tracking-widest mb-4">
                <Link to="/services" className="hover:text-white transition-colors cursor-pointer">Services</Link>
                <i className="ri-arrow-right-s-line"></i>
                Tree Removal
              </div>
              <h1 className="text-5xl lg:text-6xl font-black text-white mb-4">
                Tree Removal in Northern Virginia
              </h1>
              <p className="text-white/80 text-xl mb-8">
                Safe, efficient removal of any tree — dead, diseased, storm-damaged, or hazardous. Licensed, insured, and local.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="flex items-center justify-center gap-2 px-8 py-4 bg-[#e8632a] text-white font-black rounded-lg uppercase tracking-wide hover:bg-[#d0541e] transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-file-list-3-line"></i>Get Free Estimate
                </Link>
                <a href="tel:+15407764488" className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white font-black rounded-lg uppercase tracking-wide hover:bg-white hover:text-[#1a3a2e] transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-fill"></i>(540) 776-4488
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What we do */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#e8632a] font-bold text-sm uppercase tracking-widest block mb-3">Tree Removal Service</span>
              <h2 className="text-4xl font-black text-[#1e1e1e] mb-6">Professional Tree Removal You Can Count On</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                At Diversified Tree Services, we specialize in the safe and efficient removal of trees of all sizes across Northern Virginia. Whether you have a dead oak threatening your roof, a diseased pine dropping limbs, or a tree simply in the wrong place, our ISA Certified Arborists and professional crew handle it all.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                We use precision rigging techniques, bucket trucks, and when necessary, crane removal to take down even the most challenging trees without damage to your property. Every removal includes full chip and log cleanup.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  "Hazardous & Dead Trees",
                  "Storm Damage Removal",
                  "Large & Small Trees",
                  "Crane Removal Available",
                  "Emergency 24/7 Service",
                  "Full Debris Cleanup",
                  "Lot Clearing",
                  "Tree Risk Assessment",
                ].map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-check-line text-[#e8632a] flex-shrink-0"></i>
                    <strong>{f}</strong>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden h-[420px]">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20tree%20removal%20large%20tree%20being%20cut%20sections%20lowered%20with%20ropes%20rigging%20residential%20backyard%20arborist%20crew%20working%20together%20northern%20virginia%20dramatic%20action%20shot&width=700&height=500&seq=tr002&orientation=landscape"
                alt="Professional tree removal in progress"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Pricing section */}
        <section className="py-20 bg-[#f8f6f3]">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-[#1e1e1e] mb-4">Tree Removal Pricing Guide</h2>
              <p className="text-gray-500">Pricing varies by size, location, and complexity. Get a free written estimate for your exact project.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { size: "Small Tree", height: "Up to 30 ft", price: "$350–$650", examples: "Ornamental trees, young maples, small pines", icon: "ri-plant-line" },
                { size: "Medium Tree", height: "30–60 ft", price: "$650–$1,200", examples: "Mature oaks, large maples, medium pines", icon: "ri-tree-line" },
                { size: "Large Tree", height: "60+ ft", price: "$1,200–$2,500+", examples: "Towering oaks, large dead trees, complex jobs", icon: "ri-tree-fill" },
              ].map((p) => (
                <div key={p.size} className="bg-white rounded-2xl p-8 text-center">
                  <i className={`${p.icon} text-5xl text-[#e8632a] mb-4 block`}></i>
                  <h3 className="text-xl font-black text-[#1e1e1e] mb-1">{p.size}</h3>
                  <p className="text-gray-400 text-sm mb-4">{p.height}</p>
                  <div className="text-3xl font-black text-[#e8632a] mb-3">{p.price}</div>
                  <p className="text-gray-500 text-sm">{p.examples}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-400 text-sm mt-6">*Prices are estimates. Stump grinding is an additional service. Final price based on on-site assessment.</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 lg:px-8">
            <h2 className="text-4xl font-black text-[#1e1e1e] mb-10 text-center">Tree Removal FAQs</h2>
            <div className="space-y-4">
              {FAQS.map((f) => (
                <div key={f.q} className="bg-[#f8f6f3] rounded-xl p-6">
                  <h3 className="font-bold text-[#1e1e1e] mb-2">{f.q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service area links */}
        <section className="py-12 bg-[#f8f6f3]">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h3 className="text-xl font-black text-[#1e1e1e] mb-6">Tree Removal Across Northern Virginia</h3>
            <div className="flex flex-wrap justify-center gap-2">
              {["Fredericksburg", "Stafford County", "Spotsylvania", "King George", "Culpeper", "Warrenton", "Manassas", "Woodbridge", "Dumfries"].map((a) => (
                <Link key={a} to="/service-areas" className="px-4 py-2 bg-white text-[#1e1e1e] text-sm font-medium rounded-full border border-gray-200 hover:bg-[#e8632a] hover:text-white hover:border-[#e8632a] transition-all cursor-pointer">
                  Tree Removal {a}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-[#e8632a] text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-4xl font-black text-white mb-4">Get Your Tree Removal Quote Today</h2>
            <p className="text-white/80 mb-8">Free estimates · Same-day response · Licensed & Insured</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="px-8 py-4 bg-white text-[#e8632a] font-black rounded-lg uppercase tracking-wide hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap">
                Free Estimate
              </Link>
              <a href="tel:+15407764488" className="px-8 py-4 border-2 border-white text-white font-black rounded-lg uppercase tracking-wide hover:bg-white hover:text-[#e8632a] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-phone-fill"></i>(540) 776-4488
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
